// Rating & review-count helpers.
//
// Single source of truth = the API. The backend already computes the display
// rating (review average, or the super-admin manual override when active),
// rounds it to 1 decimal, and returns it as `avgRating` (with `rating` kept as
// an alias) plus `reviewCount`. The client must NOT re-average reviews — doing
// so was the cause of listing vs detail disagreeing. These helpers only read
// and format those fields.

/** Format any rating-ish value to a "4.5" style 1-decimal string. */
export function formatRating(value) {
  if (value == null || value === '') return '0.0';
  const n = Number(value);
  if (Number.isNaN(n)) return '0.0';
  return n.toFixed(1);
}

/**
 * Category score pill color from the reviews firm-table legend.
 * Green 4.2–5.0 · Yellow 3.0–4.1 · Red &lt; 3.0
 * @returns {string|null} hex color, or null when score is missing/invalid
 */
export function getCategoryScoreColor(score) {
  if (score == null || score === '') return null;
  const n = Number(score);
  if (!Number.isFinite(n)) return null;
  if (n >= 4.2) return '#6ED828';
  if (n >= 3.0) return '#FEFD02';
  return '#E61430';
}

/**
 * Canonical firm rating for display. Reads the server-resolved `avgRating`
 * (falls back to the `rating` alias), already override-applied and rounded.
 */
export function getFirmRating(firm) {
  return formatRating(firm?.avgRating ?? firm?.rating);
}

/** Canonical approved-review count for a firm, straight from the API. */
export function getFirmReviewCount(firm) {
  return firm?.reviewCount ?? 0;
}

/**
 * Display rating for SEO surfaces — metadata, OG images, comparison tables — or
 * `null` when the firm has no approved reviews yet.
 *
 * The API returns `avgRating: 0` (not null) for a firm with zero reviews, so a plain
 * null-check renders "0.0" and the firm reads as rated 0 out of 5 — i.e. terrible.
 * That was going out in Google snippets and every shared link preview. Anything
 * public-facing must gate the rating on `reviewCount > 0`, which is the same rule the
 * JSON-LD `aggregateRating` already uses.
 */
export function getPublicFirmRating(firm) {
  const reviewCount = Number(firm?.reviewCount) || 0;
  if (reviewCount <= 0) return null;

  const value = firm?.avgRating ?? firm?.rating;
  if (value == null || value === '') return null;

  const n = Number(value);
  if (Number.isNaN(n) || n <= 0) return null;

  return n.toFixed(1);
}

export function getReviewTitle(body) {
  if (!body?.trim()) return 'Trader Review';

  const firstSentence = body.trim().split(/(?<=[.!?])\s+/)[0];
  if (firstSentence && firstSentence.length <= 80) {
    return firstSentence;
  }

  return body.trim().slice(0, 60).trim() + (body.trim().length > 60 ? '...' : '');
}

// rating breakdown [5,4,3,2,1] => [count of 5 star, count of 4 star, ...]
// Computed from the embedded approved-reviews array (the detail/reviews
// endpoints return the full approved set, so this matches reviewCount).
export function getRatingBreakdown(reviews) {
  const breakdown = [0, 0, 0, 0, 0]; // index 0 => 5 stars, index 4 => 1 star
  if (!reviews || reviews.length === 0) return breakdown;

  reviews.forEach((r) => {
    const rating = Math.round(r.rating); // make sure it's an integer 1–5
    if (rating >= 1 && rating <= 5) {
      breakdown[5 - rating] += 1;
    }
  });

  return breakdown;
}
